import { useVmSizeCheck } from '@/hooks/useVmSizeCheck'
import { useInfrastructureWizard } from '@/stores/infrastructureWizard'
import type { VmSizeCheck, VmSizeChoice } from '@/types/infrastructure'
import { choiceOf, sizeWhere } from '@/utils/vmSizes'
import styles from './Infrastructure.module.css'
import { OptionCard } from './OptionCard'

interface VmSizeBannerProps {
  /** The number of RDS VMs: each needs its own vCPU quota, as in the size check itself. Left out, it is the number the user chose. */
  requiredIps?: number
}

const list = { margin: '6px 0 0', paddingLeft: 18 } as const

interface VmSizeChoiceCardProps {
  size: VmSizeCheck
  /** " in North Central US", or " in the chosen region". */
  where: string
  onRecheck: () => void
  checking: boolean
}

/**
 * A size that cannot be built as shown, and the nearest series of the SAME size that can: the user is asked whether to continue with it or
 * to wait (ask Platform Engineering to raise the quota, or choose another region). Nothing else is built, and Next waits until
 * "continue" is chosen. The choice counts only for this region and these two sizes (utils/vmSizes.ts `choiceOf`).
 */
function VmSizeChoiceCard({ size, where, onRecheck, checking }: VmSizeChoiceCardProps) {
  const choiceWhere = useInfrastructureWizard((s) => sizeWhere(s))
  const choice = useInfrastructureWizard((s) => choiceOf(size, s.vmSizeChoices, sizeWhere(s)))
  const chooseVmSize = useInfrastructureWizard((s) => s.chooseVmSize)
  const quota = size.cause === 'quota'
  const name = `vm-size-choice-${size.tier}`
  const choose = (value: string) =>
    chooseVmSize(size.tier, { requested: size.requested, effective: size.effective, where: choiceWhere, choice: value as VmSizeChoice })
  return (
    <div className={styles.notice} role="group" aria-label={`${size.label} size`} data-testid={`vm-size-choice-${size.tier}`}>
      <div style={{ flex: 1 }}>
        <strong>
          {size.label} size {size.requested} cannot be built{where}.
        </strong>
        <div style={{ marginTop: 4 }}>{size.message}</div>
        <fieldset className={styles.cardGroup} role="radiogroup" aria-required="true" style={{ marginTop: 10 }}>
          <legend className={styles.legend}>{size.label}: can we use {size.effective} instead?</legend>
          <OptionCard
            name={name}
            value="use"
            checked={choice === 'use'}
            onChange={choose}
            title={`Yes, use ${size.effective}`}
            description={`Build the ${size.label} as ${size.effective} instead: ${size.vcpus} vCPU, ${size.memoryGb} GB RAM, the same size as ${size.requested}.`}
          />
          <OptionCard
            name={name}
            value="wait"
            checked={choice === 'wait'}
            onChange={choose}
            title={quota ? 'No, ask Platform Engineering to raise the quota' : 'No, choose another region'}
            description={
              quota
                ? `Keep ${size.requested}. Ask Platform Engineering to raise the vCPU quota for it${where}, then check again.`
                : `Keep ${size.requested}. Go back to the Location step and choose a region where it can be built.`
            }
          />
        </fieldset>
        {choice === 'use' && (
          <p className={styles.note} data-testid={`vm-size-choice-${size.tier}-use`}>
            The {size.label} will be built as {size.effective} instead of {size.requested}.
          </p>
        )}
        {choice === 'wait' && (
          <p className={styles.note} data-testid={`vm-size-choice-${size.tier}-wait`}>
            {quota ? (
              <>
                Nothing is built until {size.requested} can be. Once Platform Engineering has raised the quota,{' '}
                <button type="button" className={styles.linkButton} onClick={onRecheck} disabled={checking}>
                  {checking ? 'Checking…' : 'Check again'}
                </button>
                .
              </>
            ) : (
              <>Nothing is built until {size.requested} can be. This check runs again by itself when the region is changed.</>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * What the user is told, at the top of the Infrastructure part, about the VM sizes of the servers that will be built in the chosen region.
 * The sizes come from the product's catalog; where the subscription cannot build one there (not offered, restricted, or no vCPU quota
 * left) the nearest series of the SAME size (the same vCPUs and memory) is offered for each server, and the user chooses: continue with it,
 * or ask Platform Engineering to raise the quota (or choose another region). Nothing is built until they choose to continue, and the Servers
 * step waits. A size nothing can replace says so and the Servers step does not continue. When Azure could not be asked, this says that, and
 * why, instead of saying nothing: a size that is not available fails the deployment when it is built. Nothing is shown when every size is
 * available, or when no new server is built. Each server's own card, further down the Servers step, repeats the reason.
 */
export function VmSizeBanner({ requiredIps }: VmSizeBannerProps) {
  const regionLabel = useInfrastructureWizard((s) => s.region?.label)
  const { sizes, note, recheck, isChecking } = useVmSizeCheck(requiredIps)
  const where = regionLabel ? ` in ${regionLabel}` : ' in the chosen region'
  const unavailable = sizes.filter((size) => size.status === 'unavailable')
  const substituted = sizes.filter((size) => size.status === 'substituted')
  const unchecked = sizes.filter((size) => size.status === 'unchecked')
  const couldNotCheck = unavailable.length === 0 && substituted.length === 0 && unchecked.length > 0 && note !== ''

  return (
    <>
      {unavailable.length > 0 && (
        <div className={styles.alert} role="alert" data-testid="vm-size-banner-unavailable">
          <div>
            <strong>This VM size cannot be built{where}.</strong>
            <ul style={list}>
              {unavailable.map((size) => (
                <li key={size.tier}>{size.message}</li>
              ))}
            </ul>
            <div style={{ marginTop: 6 }}>Choose another region to continue.</div>
          </div>
        </div>
      )}
      {substituted.map((size) => (
        <VmSizeChoiceCard key={size.tier} size={size} where={where} onRecheck={recheck} checking={isChecking} />
      ))}
      {couldNotCheck && (
        <div className={styles.notice} role="status" data-testid="vm-size-banner-unchecked">
          <div>
            <strong>Could not check which VM series are available{where}.</strong>
            <div style={{ marginTop: 4 }}>
              {note} The VM sizes are built as shown; if a series is not available there, the deployment fails when it is built.
            </div>
          </div>
        </div>
      )}
    </>
  )
}
